import { type RefObject, useEffect } from 'react';
import {
  rescaleBubbles,
  stepAll,
  type Bubble,
  type Vec2,
} from '@/utils/bubble-physics';
import { readBubbleAnchors } from '@/utils/bubble-anchors';

type Args = {
  containerRef: RefObject<HTMLDivElement | null>;
  elementRefs: RefObject<(HTMLDivElement | null)[]>;
  bubbles: Bubble[];
};

const OFFSCREEN: Vec2 = { x: -9999, y: -9999 };

function trackCursor(container: HTMLElement, cursor: Vec2): () => void {
  const onMove = (e: MouseEvent): void => {
    const r = container.getBoundingClientRect();
    cursor.x = e.clientX - r.left;
    cursor.y = e.clientY - r.top;
  };
  const onLeave = (): void => {
    cursor.x = OFFSCREEN.x;
    cursor.y = OFFSCREEN.y;
  };
  globalThis.window.addEventListener('mousemove', onMove);
  globalThis.window.addEventListener('mouseleave', onLeave);
  return () => {
    globalThis.window.removeEventListener('mousemove', onMove);
    globalThis.window.removeEventListener('mouseleave', onLeave);
  };
}

function paintAll(args: Args): void {
  for (let i = 0; i < args.bubbles.length; i++) {
    const b = args.bubbles[i];
    const el = args.elementRefs.current?.[i];
    if (!b || !el) continue;
    el.style.transform = `translate3d(${b.pos.x}px, ${b.pos.y}px, 0)`;
  }
}

type Frame = { args: Args; cursor: Vec2; container: HTMLDivElement };

type Size = { w: number; h: number };

function syncResize(bubbles: Bubble[], prev: Size, next: Size): void {
  if (prev.w > 0 && prev.h > 0 && (next.w !== prev.w || next.h !== prev.h)) {
    rescaleBubbles(bubbles, next.w / prev.w, next.h / prev.h);
  }
  prev.w = next.w;
  prev.h = next.h;
}

function startLoop(f: Frame): () => void {
  let raf = 0;
  const prev: Size = { w: 0, h: 0 };
  const tick = (now: number): void => {
    const w = f.container.clientWidth;
    const h = f.container.clientHeight;
    syncResize(f.args.bubbles, prev, { w, h });
    const ctx = {
      cursor: f.cursor,
      anchors: readBubbleAnchors(f.container),
      width: w,
      height: h,
      time: now,
    };
    stepAll(f.args.bubbles, ctx);
    paintAll(f.args);
    raf = globalThis.requestAnimationFrame(tick);
  };
  raf = globalThis.requestAnimationFrame(tick);
  return () => globalThis.cancelAnimationFrame(raf);
}

export function useBubblePhysics(args: Args): void {
  useEffect(() => {
    const container = args.containerRef.current;
    if (!container || args.bubbles.length === 0) return;
    const cursor: Vec2 = { x: OFFSCREEN.x, y: OFFSCREEN.y };
    const stopCursor = trackCursor(container, cursor);
    const stopLoop = startLoop({ args, cursor, container });
    return () => {
      stopCursor();
      stopLoop();
    };
  }, [args]);
}
